import { useEffect, useMemo, useRef, useState } from "react";

import { useApolloClient } from "@apollo/client/react";

import { ConversationMessageDocument } from "@app_schema";

import { useActionCableSubscription } from "@application/hooks/use_action_cable_subscription";

import { appendMessage } from "./append_message";
import { coalesce, heldVersions, reconnected } from "./live_updates";

// Follows a chat live. An update names a message, which is fetched alone and
// added to the thread if it's new, unless its version is already held. A
// reconnect reloads the thread, since updates may have been missed while the
// channel was down.
export const useLiveThread = ({
  id,
  messageIds,
  refetch,
}: {
  id: string;
  messageIds: string[];
  refetch: () => Promise<unknown>;
}) => {
  const client = useApolloClient();
  const latest = useRef({ id, messageIds, refetch });
  latest.current = { id, messageIds, refetch };
  // Message id to the version its update named; undefined fetches regardless.
  const named = useRef(new Map<string, string | undefined>());
  const whole = useRef(false);
  const [versions] = useState(() => heldVersions());

  const [update] = useState(() =>
    coalesce(() => {
      const { id, messageIds, refetch } = latest.current;
      const pending = [...named.current];
      named.current.clear();
      const reload = whole.current || pending.length === 0;
      whole.current = false;
      if (reload) return refetch();
      const ids = pending
        .filter(([messageId, version]) => !versions.holds(messageId, version))
        .map(([messageId]) => messageId);
      return Promise.all(
        ids.map(async (messageId) => {
          const { data } = await client.query({
            query: ConversationMessageDocument,
            variables: { id, messageId },
            fetchPolicy: "network-only",
          });
          const message = data?.conversation.message;
          versions.hold(messageId, message?.version);
          if (!messageIds.includes(messageId)) {
            appendMessage(client.cache, id, message);
          }
        }),
      );
    }),
  );

  const channel = useMemo(() => ({ channel: "ConversationChannel", id }), [id]);
  const status = useActionCableSubscription<{
    message_id?: number;
    version?: string;
  }>(channel, (data) => {
    if (data.message_id != null) {
      const messageId = String(data.message_id);
      if (versions.holds(messageId, data.version)) return;
      const unversioned =
        named.current.has(messageId) && named.current.get(messageId) == null;
      named.current.set(messageId, unversioned ? undefined : data.version);
    }
    update();
  });

  const [isReconnect] = useState(() => reconnected());
  useEffect(() => {
    if (!isReconnect(status)) return;
    whole.current = true;
    update();
  }, [status, isReconnect, update]);

  return status;
};
